// @givmo/passkeys — the account reset's three routes (AS-WEB13): AS-BE7's delayed, email-verified
// reset, extended to passkeys by SEC2-AS and served under passkeys alone by AS-BE47; and its immediate
// sibling, a saved recovery code (AS-WEB18).
//
//   - `POST /authn/mfa/factor-reset/request` `{email}` → 202 with an empty body, WHATEVER the
//     address: the code goes only to that account's own inbox, and only when the account holds a
//     factor to reset (enumeration safety);
//   - `POST /authn/mfa/factor-reset/verify` `{email, code}` → `{status: "pending", executes_at}` on the
//     first proof (it ARMS the 24-hour wait and alerts the owner on every channel), the same
//     deadline again before it, and `{status: "completed"}` at or after it (every passkey and the
//     authenticator app removed, recovery codes cleared). ONE endpoint called twice: the clock is the
//     server's, never counted here, and each call spends a fresh emailed code;
//   - `DELETE /users/me/security/factor-reset` → the security read, the reset disarmed. Idempotent;
//   - `POST /authn/mfa/recovery/redeem` `{email, code}` → 204: the code is spent and the account's
//     authenticator app is removed AT ONCE (AS-BE7's immediate route: a 60-bit secret we issued is the
//     proof). It removes the authenticator ONLY — never a passkey — and it 404s while authenticator
//     apps are dark (`ACCOUNT_SECURITY_MFA_ENABLED`), whatever the passkey flag says (AS-BE47).
//
// ⚠ THE TWO `/authn` ROUTES ARE NOT IN THE COMMITTED SNAPSHOT: the backend mounts `/authn` as its own
// sub-app, exactly as for the passkey sign-in, so they ride the sanctioned `givmoClient.post({ url })`
// (agent_docs/api-client.md § A whole ROUTE the snapshot lacks) and inherit the interceptor stack.
// They are UNAUTHENTICATED by design — a locked-out person holds no session — so the Bearer a
// signed-in console sends anyway is inert there. The cancel is the authenticated, generated route.
//
// ⚠ A FAILED PROOF IS ONE UNIFORM 400 (`MFA_RECOVERY_VERIFICATION_FAILED`, the server's own "That code
// didn’t work. Check it and try again.", APPROVED by D-AS-68 fix 7) for an unknown address, a
// wrong, spent or expired code alike — and, for a recovery code, for an account with none left: the
// server never says which; a 429 is the one distinguishable refusal; a 404 is the surface dark
// here. Each reaches the caller as the api-client's `GivmoError`, untouched.

import { givmoClient, sdk, type UserSecurityOut } from '@givmo/api-client';

const REQUEST_URL = '/authn/mfa/factor-reset/request';
const VERIFY_URL = '/authn/mfa/factor-reset/verify';
const REDEEM_URL = '/authn/mfa/recovery/redeem';

/** Where a verified code left the reset. */
export type FactorResetStatus =
  | { readonly status: 'pending'; readonly executesAt: Date | null }
  | { readonly status: 'completed' };

/** A verify answered 200 with a body this client cannot read. */
export class FactorResetUnreadableError extends Error {
  constructor() {
    super('The reset answered with a body this client cannot read.');
    this.name = 'FactorResetUnreadableError';
    Object.setPrototypeOf(this, FactorResetUnreadableError.prototype);
  }
}

function parsedDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The verify body, read tolerantly: `null` for any shape that is neither state. Never throws. */
export function factorResetStatusOf(body: unknown): FactorResetStatus | null {
  if (typeof body !== 'object' || body === null) return null;
  const { status, executes_at: executesAt } = body as { status?: unknown; executes_at?: unknown };
  if (status === 'completed') return { status: 'completed' };
  if (status === 'pending') return { status: 'pending', executesAt: parsedDate(executesAt) };
  return null;
}

/** Ask for a code to the account's address (always 202: nothing here says whether one went out). */
export async function requestFactorReset(
  email: string,
  init?: { signal?: AbortSignal },
): Promise<void> {
  const { error } = await givmoClient.post({
    url: REQUEST_URL,
    body: { email },
    signal: init?.signal,
  });
  if (error !== undefined && error !== null) throw error;
}

/** Spend one emailed code: arm the reset, or, at or after its deadline, finish it. */
export async function verifyFactorReset(
  email: string,
  code: string,
  init?: { signal?: AbortSignal },
): Promise<FactorResetStatus> {
  const { data, error } = await givmoClient.post({
    url: VERIFY_URL,
    body: { email, code },
    signal: init?.signal,
  });
  if (error !== undefined && error !== null) throw error;
  const status = factorResetStatusOf(data);
  if (status === null) throw new FactorResetUnreadableError();
  return status;
}

/** "Wasn't me — cancel it": disarm a pending reset, answering the account's security read. */
export async function cancelFactorReset(): Promise<UserSecurityOut> {
  const { data } = await sdk.userSecurityCancelMyFactorReset({ throwOnError: true });
  return data;
}

/**
 * Spend one saved recovery code: the account's authenticator app is removed at once (204). The code
 * goes as typed — the server accepts the display form or bare, and normalizes case and the
 * look-alike characters — with only the edges' whitespace trimmed.
 */
export async function redeemRecoveryCode(
  email: string,
  code: string,
  init?: { signal?: AbortSignal },
): Promise<void> {
  const { error } = await givmoClient.post({
    url: REDEEM_URL,
    body: { email, code: code.trim() },
    signal: init?.signal,
  });
  if (error !== undefined && error !== null) throw error;
}
